import { appState } from '@/store/appState'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/ToastProvider'
import { GoogleMapSelectMode } from '@/components/GoogleMapSelectMode'
import { startGetingCurrentUserLocation } from '@/custom/startGetingCurrentUserLocation'
import { isPossitionValid } from '@/lib/validate'
import { dateTimeFormat } from '@/lib/format'
import { tr } from '@/i18n'
import type { SheetProps } from './index'

/* The `initial` view (actions_bottom_sheet_widget.dart:69-333). */

interface Props extends SheetProps {
  send: (delayMs: 2000 | 1000) => Promise<void>
  forceRender: () => void
}

export function InitialView({ userName, alerts, send, forceRender }: Props) {
  const toast = useToast()

  const activatedText = tr.sheet.activatedRow(
    alerts != null && alerts.length > 0
      ? dateTimeFormat('relative', alerts[0]?.data.createdAt)
      : tr.sheet.activatedUnknown,
  )

  return (
    <>
      {/* Padding(14,22,14,0) -> mode switch */}
      <div style={{ width: '100%', padding: '22px 14px 0' }}>
        <GoogleMapSelectMode />
      </div>

      {/* Padding(12,6,12,0) -> activated row + "see history". Flutter Row
          centres its children vertically (default crossAxisAlignment) —
          restored parity (was flex-start). DELIBERATE DEVIATION
          (user-approved): the original let a long timeago wrap to a second
          line inside Expanded; here the row is forced to ONE line and the
          timeago side truncates with an ellipsis. */}
      <div
        style={{
          width: '100%',
          padding: '6px 12px 0',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span
          data-activated-row
          style={{
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: 'var(--font-poppins-regular)',
            fontWeight: 400,
            fontSize: 14,
            color: 'var(--muted-text)',
          }}
        >
          {activatedText}
        </span>
        <button
          type="button"
          data-see-history
          onClick={() => {
            // WITHOUT update(): only the sheet re-renders; page & map stale.
            appState.currentBottomSheetView = 'history'
            forceRender()
          }}
          style={{
            padding: '4px 0',
            flexShrink: 0, // Flutter: non-Expanded Row children never shrink
            whiteSpace: 'nowrap',
            fontFamily: 'var(--font-poppins-semibold)',
            fontWeight: 600,
            fontSize: 14,
            color: 'var(--muted-text)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {tr.sheet.seeHistory}
        </button>
      </div>

      {/* Padding(14,16,14,0) -> maxWidth 300 message (trailing space kept) */}
      <div style={{ padding: '16px 14px 0', display: 'flex', justifyContent: 'center' }}>
        <p
          data-unsafe-text
          style={{
            maxWidth: 300,
            fontFamily: 'var(--font-poppins-regular)',
            fontWeight: 400,
            fontSize: 20,
            color: '#000000',
            textAlign: 'center',
          }}
        >
          {tr.sheet.feelsUnsafe(userName)}
        </p>
      </div>

      {/* Padding(0,20,0,0) */}
      <div style={{ paddingTop: 20 }}>
        <Button text={tr.sheet.sendReassurance} onClick={() => send(2000)} />
      </div>

      {/* Padding(0,12,0,30) */}
      <div style={{ padding: '12px 0 30px' }}>
        <Button
          text={tr.sheet.getDirections}
          variant="outlinedLight"
          onClick={async () => {
            await startGetingCurrentUserLocation(false)
            if (isPossitionValid(appState.myPosition)) {
              appState.currentBottomSheetView = 'directions'
              appState.update() // publishes
            } else {
              toast.show(tr.toasts.turnOnGeolocation)
            }
          }}
        />
      </div>
    </>
  )
}
